import React, { useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import '../styles/WhyCVEarity.css';

gsap.registerPlugin(ScrollTrigger);

const stats = [
  {
    num: '1 in 3',
    label: 'breaches stem from known unpatched CVEs',
    sub: 'Major global cyber incidents were triggered by publicly known vulnerabilities that organizations overlooked.',
  },
  {
    num: 'Hours',
    label: 'not months - attackers exploit new CVEs',
    sub: 'Attackers exploit new CVEs within hours of disclosure. That gap is where damage happens.',
  },
  {
    num: '240k+',
    label: 'CVEs published and growing every week',
    sub: 'Without prioritization, every alert feels equally urgent. Nothing gets fixed in time.',
  },
];

const features = [
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M10 2L18 6V10C18 14.4 14.4 18.4 10 19C5.6 18.4 2 14.4 2 10V6L10 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M7 10L9 12L13 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    title: 'Real-time CVE ingestion',
    desc: 'Pulled directly from NVD, MITRE, and vendor feeds the moment they publish.',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M3 7L10 3L17 7V13L10 17L3 13V7Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M10 3V17M3 7L17 13M17 7L3 13" stroke="currentColor" strokeWidth="1" opacity="0.4"/>
      </svg>
    ),
    title: 'Severity-first prioritization',
    desc: 'CVSS v3 scoring with contextual enrichment - know exactly what to patch first.',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <rect x="2" y="3" width="16" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M6 8H14M6 11H11M6 14H9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Automated stack matching',
    desc: "Tell us your tech stack once. We map every relevant CVE to your exact environment.",
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M10 2C5.6 2 2 5.6 2 10s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8z" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M10 6V10L13 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Continuous monitoring',
    desc: 'Our system continuously observes global vulnerability trends and emerging threat patterns.',
  },
];

export default function WhyCVEarity() {
  const ref = useRef(null);
  const sectionRef = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.utils.toArray('.why__stat').forEach((stat, i) => {
        gsap.from(stat, {
          y: 40,
          opacity: 0,
          duration: 0.7,
          delay: i * 0.15,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: stat,
            start: 'top 85%',
          },
        });
      });

      gsap.utils.toArray('.why__feature').forEach((feat, i) => {
        gsap.from(feat, {
          x: -30,
          opacity: 0,
          duration: 0.6,
          delay: i * 0.1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: feat,
            start: 'top 90%',
          },
        });
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section id="why" className="why section" ref={sectionRef}>
      <div className="container">
        <div className="why__layout">
          <motion.div
            ref={ref}
            className="why__left"
            initial={{ opacity: 0, x: -24 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.55 }}
          >
            <div className="section-eyebrow">Why CVEarity?</div>
            <h2 className="why__h2">
              Threat context<br />
              <span className="why__h2-accent">your team is missing</span>
            </h2>
            <p className="why__intro">
              Security teams aren't short on data. They're short on signal.
              CVEarity was built to be the Bloomberg Terminal of vulnerability
              intelligence - dense with meaning, effortless to navigate.
            </p>

            <ul className="why__features">
              {features.map((f, i) => (
                <li key={i} className="why__feature">
                  <span className="why__feature-icon">{f.icon}</span>
                  <div>
                    <div className="why__feature-title">{f.title}</div>
                    <div className="why__feature-desc">{f.desc}</div>
                  </div>
                </li>
              ))}
            </ul>

            <motion.div
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ delay: 0.55 }}
            >
              <a href="https://app.cvearity.com" target="_blank" rel="noopener noreferrer" className="btn-primary">
                Launch App →
              </a>
            </motion.div>
          </motion.div>

          <div className="why__right">
            {stats.map((s, i) => (
              <div key={i} className="why__stat">
                <div className="why__stat-num font-mono">{s.num}</div>
                <div className="why__stat-label">{s.label}</div>
                <div className="why__stat-sub">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
